import { useState } from 'react';
import { Link } from 'wouter';
import { Home, Users, Calendar, MessageSquare, LayoutGrid, Search, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { COACH_NAV_GROUPS, STANDARD_ROUTES } from '@/config/standardRoutes';
import { CalendarGlyph, ClientsGlyph, InboxGlyph, ShieldCheckGlyph } from './TabIcons';
import { usePhoneLayout } from './usePhoneLayout';
import '@/styles/iphone-shell.css';

const groups = COACH_NAV_GROUPS.filter(group => group.label !== 'My Training');
const listed = new Set(groups.flatMap(group => group.items.map(item => item.path)));
export const COMMAND_TOOL_GROUPS = [...groups, { label: 'More coaching tools', items: STANDARD_ROUTES.filter(route =>
  route.path.startsWith('/coach') && !route.path.startsWith('/coach/my/') && route.path !== '/coach/wins' &&
  !listed.has(route.path) && ['primary', 'direct'].includes(route.exposure) && !route.path.includes(':')) }];
export function CommandTools({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const visible = COMMAND_TOOL_GROUPS.map(group => ({ ...group, items: group.items.filter(item => `${item.title} ${item.navLabel ?? ''} ${group.label}`.toLowerCase().includes(search.toLowerCase())) }));
  return <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
    <DialogContent className="standard-entry-sheet" aria-describedby={undefined}>
      <DialogTitle>All coaching tools</DialogTitle>
      <label className="standard-search"><Search size={19}/><input aria-label="Search coaching tools" placeholder="Find a coaching tool…" value={search} onChange={event => setSearch(event.target.value)}/></label>
      <div className="standard-sheet-scroll">
        {visible.map(group => group.items.length > 0 && <section key={group.label} className="command-tool-group"><h3>{group.label}</h3>{group.items.map(item => <Link href={item.path} key={item.path} onClick={onClose} className="command-tool-link"><span>{item.navLabel ?? item.title}</span><ChevronRight size={18}/></Link>)}</section>)}
        {!visible.some(group => group.items.length > 0) && <p>No tools match that search.</p>}
      </div>
    </DialogContent>
  </Dialog>;
}

const PHONE_COACH_TABS = [
  { label: 'Command', href: '/coach', Glyph: ShieldCheckGlyph },
  { label: 'Clients', href: '/coach/clients', Glyph: ClientsGlyph },
  { label: 'Inbox', href: '/coach/inbox', Glyph: InboxGlyph },
  { label: 'Schedule', href: '/coach/schedule', Glyph: CalendarGlyph },
] as const;

/** iPhone coach tab bar: Command, Clients, Inbox and Schedule. Every coaching
 * tool stays reachable from the menu drawer and the Command dashboard. The
 * coach's own training is one tap away on the top bar's Training and Command
 * switch, the only mode switch. */
function PhoneCommandNav({ location, unreadMessages }: { location: string; unreadMessages: number }) {
  return <nav className="command-mobile-nav app-mobile-bottom-nav iphone-tabbar iphone-tabbar--coach xl:hidden" aria-label="Coach navigation" data-phone-tabs="true">
    {PHONE_COACH_TABS.map(({ label, href, Glyph }) => {
      const active = href === '/coach' ? location === href : location.startsWith(href);
      const unread = href === '/coach/inbox' && unreadMessages > 0 ? Math.min(99, unreadMessages) : 0;
      return <Link key={href} href={href} aria-current={active ? 'page' : undefined} data-active={active ? 'true' : undefined}
        aria-label={unread ? `${label}, ${unread} unread` : undefined} className="iphone-tabbar__item">
        <span className="iphone-tabbar__pill"><Glyph size={24} />{unread ? <b aria-hidden="true">{unread}</b> : null}</span>
        <span className="iphone-tabbar__label">{label}</span>
      </Link>;
    })}
  </nav>;
}

export function CommandMobileNav({ location, unreadMessages }: { location: string; unreadMessages: number }) {
  const phone = usePhoneLayout();
  const [open, setOpen] = useState(false);
  if (phone) return <PhoneCommandNav location={location} unreadMessages={unreadMessages} />;
  return <>
    <nav className="command-mobile-nav app-mobile-bottom-nav xl:hidden" aria-label="Coach navigation">
      {[[Home, 'Dashboard', '/coach'], [Users, 'Clients', '/coach/clients'], [Calendar, 'Schedule', '/coach/schedule'], [MessageSquare, 'Inbox', '/coach/inbox']].map(([Icon, label, href]) => {
        const Glyph = Icon as typeof Home;
        const active = href === '/coach' ? location === href : location.startsWith(href as string);
        return <Link key={href as string} href={href as string} aria-current={active ? 'page' : undefined}><Glyph size={21}/><span>{label as string}</span>{href === '/coach/inbox' && unreadMessages > 0 && <b>{Math.min(99, unreadMessages)}</b>}</Link>;
      })}
      <button onClick={() => setOpen(true)} aria-expanded={open}><LayoutGrid size={21}/><span>All tools</span></button>
    </nav>
    <CommandTools open={open} onClose={() => setOpen(false)}/>
  </>;
}
